<template>
  <!-- COL:{{props.cardsGroup?.columns}} -->
  <div
    class="cards-list"
    :style="[
      CssStyleHelpers.gap(cardsGroup?.cardsBetweenSpacing || 0),
      CssStyleHelpers.paddings(cardsGroup?.cardsOuterSpacing || 0),
      gridColumnsStyle
    ]"
  >
    <div
      v-for="(card, index) in cards"
      :key="index"
      class="card-container "
      :class="getCardClass(index)"
      :style="reverseCheckboxOrderStyle"
    >
      <CheckBox
        :model-value="isSelected(index)"
        @update:model-value="toggleCard(index)"
        :checkbox-color="cardsGroup?.checkboxRadioColor"
        :checkbox-border-color="cardsGroup?.checkboxRadioBorderColor"
      />
      <!-- <pre   class="text-black"> {{ cardsGroup?.reverseCheckboxOrder }} </pre> -->
      <div 
        @click="toggleCard(index)" 
        :class="['card-clickable', { 'p-4': hasPadding, 'rounded-lg': hasRounded, 'bg-white': hasWhiteBackground, 'card-shadow': hasCardShadow }, cardsGroup?.reverseCheckboxOrder ? 'mr-4': 'ml-6']"
      >
        <HorizontalCard
          :card="card"
          :cards-group="cardsGroup"
          :main-group="mainGroup"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';
import { CssStyleHelpers } from "../../utils/cssStyleHelpers"; // Динамічні відступи між картками
import CheckBox from '../../vendor/CheckBox.vue';
import HorizontalCard from '../HorizontalCard.vue';

const props = defineProps<{
  cards: any[];
  cardsGroup: any;
  mainGroup: any;
  trueAnswers?: number[];
  userAnswers?: number[];
  showAnswers?: boolean;
}>();

const emit = defineEmits<{
  'update:userAnswers': [answers: number[]];
}>();

const selectedCardIds = ref<number[]>([]);
const isMobile = ref(false);
const isSmallScreen = ref(false);

function updateIsMobile() {
  isMobile.value = window.innerWidth <= 600; // Значення 600px підібране для мобільного режиму
  isSmallScreen.value = window.innerWidth < 720; // Для екранів менше 720px - 1 стовпчик
}

onMounted(() => {
  updateIsMobile();
  window.addEventListener('resize', updateIsMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIsMobile);
});

const columnsCount = computed(() => {
  // На екранах менше 720px завжди 1 стовпчик
  if (isSmallScreen.value) {
    return 1;
  }
  const source = props.cardsGroup?.columns;
  const raw =
    typeof source === "object" && source !== null
      ? (source as { value?: unknown }).value
      : source;
  const parsed = parseInt(String(raw ?? ""), 10);
  const normalized = Number.isFinite(parsed) && parsed > 0 ? parsed : 2;
  return isMobile.value && normalized > 2 ? 2 : normalized;
});

const gridColumnsStyle = computed(() => ({
  "--cards-columns": String(columnsCount.value),
  gridTemplateColumns: `repeat(${columnsCount.value}, minmax(0, 1fr))`,
}));

const reverseCheckboxOrderStyle = computed(() => {
  const isReversed = props.cardsGroup?.reverseCheckboxOrder;
  return (isReversed ? { flexDirection: 'row-reverse' } : {}) as Record<string, any>;
});

const hasShadow = computed(() => {
  return props.cardsGroup?.isShowShadow === true;
});

const hasPadding = computed(() => {
  return props.cardsGroup?.isShowCardPadding !== false;
});

const hasRounded = computed(() => {
  return props.cardsGroup?.isShowCardRounded !== false;
});

const hasWhiteBackground = computed(() => {
  return hasShadow.value && props.cardsGroup?.isShowCardWhiteBackground !== false;
});

const hasCardShadow = computed(() => {
  return hasShadow.value;
});

// Инициализируем выбранные карточки из userAnswers
if (props.userAnswers && props.userAnswers.length > 0) {
  selectedCardIds.value = [...props.userAnswers];
}

// Следим за изменениями props.userAnswers (обновление извне)
watch(() => props.userAnswers, (newAnswers) => {
  const newValue = newAnswers && newAnswers.length > 0 ? [...newAnswers] : [];
  // Обновляем только если массив изменился
  if (JSON.stringify(newValue) !== JSON.stringify(selectedCardIds.value)) {
    selectedCardIds.value = newValue;
  }
}, { deep: true });

function isSelected(index: number): boolean {
  return selectedCardIds.value.includes(index);
}

function toggleCard(index: number) {
  const selectedIndex = selectedCardIds.value.indexOf(index);
  if (selectedIndex > -1) {
    selectedCardIds.value.splice(selectedIndex, 1);
  } else {
    selectedCardIds.value.push(index);
  }
}

// Следим за изменениями selectedCardIds и обновляем userAnswers
watch(selectedCardIds, (newValue) => {
  emit('update:userAnswers', [...newValue]);
}, { deep: true });

// Определение класса для карточки
function getCardClass(index: number) {
  if (!props.showAnswers) {
    // Если не показываем ответы, не подсвечиваем ничего
    return {};
  }
  
  const isUserAnswer = props.userAnswers?.includes(index);
  const isTrueAnswer = props.trueAnswers?.includes(index);
  
  if (isUserAnswer && isTrueAnswer) {
    return { 'card-correct-user': true };
  } else if (isUserAnswer && !isTrueAnswer) {
    return { 'card-incorrect-user': true };
  } else if (!isUserAnswer && isTrueAnswer) {
    return { 'card-correct': true };
  }
  
  return {};
}
</script>

<style scoped>
.cards-list {
  display: grid;
  grid-template-columns: repeat(var(--cards-columns), minmax(0, 1fr));
}

/* На екранах менше 720px - 1 стовпчик */
@container (max-width: 680px) {
  .cards-list {
    grid-template-columns: 1fr!important;
  }
}

.card-container {
  display: flex;
  align-items: center;
  margin-bottom: 0px; /* Відступи керуються через gap контейнера */
  border: 0px solid transparent;
  transition: all 0.2s ease;
  border-right: none;
  outline: solid 1px transparent;
  /* border-left: solid 5px transparent; */
}

/* Правильна відповідь (зелена рамка) */
.card-container.card-correct {
  background: linear-gradient(90deg, rgba(0, 191, 127, 0.1) 0%, transparent 100%);

}

/* Правильна відповідь користувача (зелена рамка) */
.card-container.card-correct-user {

  background: linear-gradient(90deg, rgba(0, 191, 127, 0.2) 0%, transparent 100%);
}

/* Неправильна відповідь користувача (червона рамка) */
.card-container.card-incorrect-user {
  background: linear-gradient(90deg, rgba(243, 34, 97, 0.2) 0%, transparent 100%);
}
.card-container.card-correct .card-clickable {
  border-left: solid 5px rgba(0, 191, 127, 1);
}
.card-container.card-correct-user .card-clickable {
  border-left: solid 5px rgba(0, 191, 127, 0.999);
}
.card-clickable {
  flex: 1;
  cursor: pointer;
  user-select: none;
}

/* Тінь як у VLessonMultiTest.vue з bibleawr-student.ui */
.card-clickable.card-shadow {
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
}
.card-container.card-correct{
  flex: row;
}
</style>

